/**
 * The organization's rules for the pull requests Airlock opens and the branches
 * it lands on by itself (`policy.pullRequests`, `org/pull-request-rules.ts` in
 * contracts), under the GitHub page of its integrations.
 *
 * Policy, like the network lists: not rows with a padlock, because nothing on a
 * member's machine sits beneath them to inherit or override — they hold on every
 * pull request Airlock opens in the organization's workspaces, from any machine.
 * Each list is checked entry by entry with the validator the Worker uses.
 */
import type React from 'react';
import { EMPTY_ORG_POLICY, policyOf, type OrgPolicy } from '@airlock/contracts/org/policy';
import { MAX_PULL_REQUEST_LIST, pullRequestEntryProblem, type OrgPullRequests } from '@airlock/contracts/org/pull-request-rules';
import { color, font, radius } from '../../theme/tokens';
import { SettingToggle } from '../../components/settings-row';
import type { OrgSettings } from '../../lib/types';
import { DomainList, ListBlock } from './list-settings';

type ListKey = 'reviewers' | 'teamReviewers' | 'protectedBranches' | 'requiredChecks';

const LISTS: { key: ListKey; label: string; hint: string; placeholder: string }[] = [
  {
    key: 'reviewers',
    label: 'Reviewers asked on every pull request',
    hint: 'GitHub logins, asked to review every pull request Airlock opens in this organization’s workspaces — on top of whoever the person names.',
    placeholder: 'octocat'
  },
  {
    key: 'teamReviewers',
    label: 'Teams asked on every pull request',
    hint: 'GitHub teams as org/team-slug, asked the same way.',
    placeholder: 'acme/web'
  },
  {
    key: 'protectedBranches',
    label: 'Branches Airlock never lands on by itself',
    hint: 'A finished session landing by itself (auto-land) refuses these, and says why. * stands for one path segment, ** for several. A person can still merge by hand.',
    placeholder: 'release/*'
  },
  {
    key: 'requiredChecks',
    label: 'Checks that must pass before Airlock lands on a branch by itself',
    hint: 'Their names as GitHub shows them. Landing by itself on an existing branch waits until each has passed on that branch’s head, in every repository of the workspace.',
    placeholder: 'build'
  }
];

const area: React.CSSProperties = {
  width: '100%',
  maxWidth: 560,
  padding: '8px 11px',
  border: `1px solid ${color.lineInput}`,
  borderRadius: radius.md,
  background: color.surface,
  fontFamily: font.mono,
  fontSize: 13,
  color: color.ink
};

export function PullRequestPolicy({
  settings,
  editable,
  onChange
}: {
  settings: OrgSettings;
  editable: boolean;
  onChange: (next: OrgPolicy) => void;
}): React.JSX.Element {
  const policy = policyOf(settings);
  // The draft as typed, not as a machine would read it: `policyOf` drops a
  // template without its {title} yet, which would empty the field under the
  // cursor. The Worker's validator says what is wrong on publish.
  const drafted = (settings.policy as { pullRequests?: OrgPullRequests } | undefined)?.pullRequests;
  const rules = drafted ? { ...policy.pullRequests, ...drafted } : policy.pullRequests;
  const put = (patch: Partial<OrgPullRequests>): void =>
    onChange({ ...EMPTY_ORG_POLICY, ...policy, pullRequests: { ...rules, ...patch } });
  return (
    <>
      {LISTS.map((list) => (
        <ListBlock key={list.key} label={list.label} hint={list.hint}>
          <DomainList
            value={rules[list.key]}
            onChange={(next) => put({ [list.key]: next })}
            editable={editable}
            placeholder={list.placeholder}
            problemOf={(entry) => pullRequestEntryProblem(list.key, entry)}
            max={MAX_PULL_REQUEST_LIST}
          />
        </ListBlock>
      ))}
      <ListBlock
        label="Title template"
        hint="{title} stands for the title the person or the agent wrote. Left empty, a title is kept as written."
      >
        {editable ? (
          <input value={rules.titleTemplate} placeholder="[WEB] {title}" onChange={(event) => put({ titleTemplate: event.target.value })} style={{ ...area, height: 34 }} />
        ) : (
          <p style={{ margin: 0, fontSize: 13, color: color.inkMuted }}>{rules.titleTemplate || 'As written.'}</p>
        )}
      </ListBlock>
      <ListBlock label="Description template" hint="{body} stands for the description that was written. Left empty, it is kept as written.">
        {editable ? (
          <textarea
            value={rules.bodyTemplate}
            rows={5}
            placeholder={'{body}\n\n## Checklist\n- [ ] Tests'}
            onChange={(event) => put({ bodyTemplate: event.target.value })}
            style={area}
          />
        ) : (
          <pre style={{ margin: 0, fontSize: 12.5, color: color.inkMuted, whiteSpace: 'pre-wrap' }}>{rules.bodyTemplate || 'As written.'}</pre>
        )}
      </ListBlock>
      <ListBlock label="Open every pull request as a draft" hint="Whatever the person or the landing chose.">
        {editable ? (
          <SettingToggle on={rules.draft} onToggle={() => put({ draft: !rules.draft })} />
        ) : (
          <p style={{ margin: 0, fontSize: 13, color: color.inkMuted }}>{rules.draft ? 'Yes' : 'No'}</p>
        )}
      </ListBlock>
    </>
  );
}
